import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { Card, CardContent, CardHeader } from '../../components/ui'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { API_ENDPOINTS } from '../../config/api'
import { Plus, Edit, Trash2, Eye, X, AlertTriangle, TrendingUp } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'

const GOVERNANCE_RISKS_LEVEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '20', label: 'Critical (20+)' },
  { value: '15', label: 'High (15-19)' },
  { value: '10', label: 'Medium (10-14)' },
  { value: '5', label: 'Low (5-9)' },
  { value: '1', label: 'Very Low (1-4)' },
]

interface Risk {
  id: string
  title: string
  description?: string
  likelihood: number
  impact: number
  level: number
  ownerName?: string
  ownerId?: string
  status: string
  nextReview?: string
  createdAt: string
  updatedAt: string
}

const GovernanceRisks: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [risks, setRisks] = useState<Risk[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null)
  const [users, setUsers] = useState<Array<{ id: string; username: string; email?: string }>>([])
  const [heatmapData, setHeatmapData] = useState<any>(null)
  const [topRisks, setTopRisks] = useState<Risk[]>([])
  const [trendlineData, setTrendlineData] = useState<any>(null)
  const [mitigationSuggestions, setMitigationSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [levelFilter, setLevelFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  useEffect(() => {
    loadRisks()
    loadUsers()
    loadHeatmap()
    loadTopRisks()
    loadTrendline()
  }, [])

  useEffect(() => {
    loadRisks()
    loadHeatmap()
    loadTopRisks()
  }, [statusFilter, levelFilter, searchQuery, fromDate, toDate])

  const loadUsers = async () => {
    try {
      const rawUser = localStorage.getItem('user')
      const userObj = rawUser ? JSON.parse(rawUser) : null
      const primary = userObj?.organizations?.[0]
      const orgId = (primary && typeof primary === 'object' && primary.id) ? primary.id : (typeof primary === 'string' ? primary : undefined)
      
      if (!orgId) {
        setUsers([])
        return
      }
      
      const response = await fetch(`/api/users?organizationId=${orgId}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setUsers(Array.isArray(data.users) ? data.users : [])
      }
    } catch (err) {
      console.error('[GovernanceRisks] Error loading users:', err)
      setUsers([])
    }
  }

  const loadRisks = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (levelFilter) params.append('level', levelFilter)
      if (searchQuery) params.append('search', searchQuery)
      if (fromDate) params.append('fromDate', fromDate)
      if (toDate) params.append('toDate', toDate)

      const url = `${API_ENDPOINTS.GOVERNANCE_RISKS}${params.toString() ? `?${params.toString()}` : ''}`
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load risks')
      const data = await res.json()
      setRisks(data.risks || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load risks')
      console.error('[GovernanceRisks] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadHeatmap = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISKS_HEATMAP, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setHeatmapData(data.matrix || null)
      }
    } catch (err) {
      console.error('[GovernanceRisks] Error loading heatmap:', err)
    }
  }

  const loadTopRisks = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISKS_TOP, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setTopRisks(data.risks || [])
      }
    } catch (err) {
      console.error('[GovernanceRisks] Error loading top risks:', err)
    }
  }

  const loadTrendline = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISKS_ANALYTICS_TREND, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setTrendlineData(data.trend || null)
      }
    } catch (err) {
      console.error('[GovernanceRisks] Error loading trendline:', err)
    }
  }

  const loadMitigationSuggestions = async (riskId: string) => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISK_MITIGATION_SUGGESTIONS(riskId), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setMitigationSuggestions(data.suggestions || [])
        setShowSuggestions(true)
      }
    } catch (err) {
      console.error('[GovernanceRisks] Error loading mitigation suggestions:', err)
    }
  }

  const handleDeleteRisk = async (id: string) => {
    if (!confirm(t('governance.risks.deleteConfirm', 'Are you sure you want to delete this risk?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISK(id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete risk')
      }

      await loadRisks()
    } catch (err: any) {
      alert(err.message || t('governance.risks.deleteError', 'Failed to delete risk'))
    }
  }

  // Calculate overview metrics
  const overview = useMemo(() => {
    const open = risks.filter(r => r.status === 'Open').length
    const mitigating = risks.filter(r => r.status === 'Mitigating').length
    const accepted = risks.filter(r => r.status === 'Accepted').length
    const closed = risks.filter(r => r.status === 'Closed').length
    const highLevel = risks.filter(r => r.level >= 15).length
    const criticalLevel = risks.filter(r => r.level >= 20).length

    return { open, mitigating, accepted, closed, highLevel, criticalLevel }
  }, [risks])

  // Get unique statuses and levels for filters
  const uniqueStatuses = useMemo(() => {
    return Array.from(new Set(risks.map(r => r.status))).sort()
  }, [risks])

  const statusFilterOptions = useMemo(
    () => uniqueStatuses.map((s) => ({ value: s, label: s })),
    [uniqueStatuses],
  )

  const getRiskLevelPillClass = (level: number) => {
    if (level >= 20) return listStyles.riskLevelCritical
    if (level >= 15) return listStyles.riskLevelHigh
    if (level >= 10) return listStyles.riskLevelMedium
    if (level >= 5) return listStyles.riskLevelLow
    return listStyles.riskLevelVeryLow
  }

  const getRiskStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Open':
        return listStyles.badgeRiskOpen
      case 'Mitigating':
        return listStyles.badgeRiskMitigating
      case 'Accepted':
        return listStyles.badgeRiskAccepted
      case 'Closed':
        return listStyles.badgeRiskClosed
      default:
        return listStyles.badgeDefault
    }
  }

  const getRiskLevelLabel = (level: number) => {
    if (level >= 20) return 'Critical'
    if (level >= 15) return 'High'
    if (level >= 10) return 'Medium'
    if (level >= 5) return 'Low'
    return 'Very Low'
  }

  const handleCreate = () => {
    setSelectedRisk(null)
    setCreateModalOpen(true)
  }

  const handleEdit = (risk: Risk) => {
    setSelectedRisk(risk)
    setEditModalOpen(true)
  }

  const handleView = (risk: Risk) => {
    setSelectedRisk(risk)
    setViewModalOpen(true)
  }

  const handleSave = async () => {
    await loadRisks()
    setCreateModalOpen(false)
    setEditModalOpen(false)
    setViewModalOpen(false)
    setSelectedRisk(null)
  }

  const clearFilters = () => {
    setStatusFilter('')
    setLevelFilter('')
    setSearchQuery('')
    setFromDate('')
    setToDate('')
  }

  if (loading && risks.length === 0) {
    return (
      <PageLayout pageScope="governance-risks-page" title={t('governance.risks.title', 'Risk Register')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="governance-risks-page" title={t('governance.risks.title', 'Risk Register')}
      actions={
        <Button
          type="button"
          color="primary"
          size="small"
          icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
          onClick={handleCreate}
        >
          {t('governance.risks.create', 'New Risk')}
        </Button>
      }
    >
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      <GovernanceListingSection title={t('governance.risks.overview', 'Risk Overview')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid6}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.open', 'Open')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{overview.open}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.mitigating', 'Mitigating')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{overview.mitigating}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.accepted', 'Accepted')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{overview.accepted}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.closed', 'Closed')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{overview.closed}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileOrange}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.highCritical', 'High / critical')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textOrange}`}>{overview.highLevel}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.risks.criticalOnly', 'Critical')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{overview.criticalLevel}</p>
          </div>
        </div>
      </GovernanceListingSection>

      <div className={`${listStyles.chartsRow} ${listStyles.sectionSpacing}`}>
        {/* Risk Heatmap */}
        <Card>
          <CardHeader>
            <h3 className={listStyles.chartCardTitle}>{t('governance.risks.heatmapTitle', 'Risk heatmap')}</h3>
          </CardHeader>
          <CardContent>
            {heatmapData ? (
              <div className="space-y-2">
                <div className="text-xs text-gray-500 mb-2 text-right">Impact →</div>
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map((impact, i) => (
                    <div key={impact} className="flex items-center gap-2">
                      <div className="w-12 text-xs text-gray-600 text-right">{impact}</div>
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3, 4, 5].map((likelihood, j) => {
                          const cell = heatmapData[i]?.[j] || { count: 0, risks: [] }
                          const level = impact * likelihood
                          const bgColor = level >= 20 ? 'bg-red-600' : level >= 15 ? 'bg-orange-500' : level >= 10 ? 'bg-yellow-400' : level >= 5 ? 'bg-blue-400' : 'bg-green-300'
                          return (
                            <div
                              key={`${impact}-${likelihood}`}
                              className={`flex-1 aspect-square border border-gray-300 rounded flex items-center justify-center text-white text-xs font-bold ${bgColor} cursor-pointer hover:opacity-80`}
                              title={`Impact: ${impact}, Likelihood: ${likelihood}, Count: ${cell.count}`}
                            >
                              {cell.count > 0 ? cell.count : ''}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-12"></div>
                  <div className="flex gap-1 flex-1">
                    {[1, 2, 3, 4, 5].map(l => (
                      <div key={l} className="flex-1 text-center text-xs text-gray-600">{l}</div>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2 text-center">← Likelihood</div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">Loading heatmap...</div>
            )}
          </CardContent>
        </Card>

        {/* Top Risks List */}
        <Card>
          <CardHeader>
            <h3 className={listStyles.chartCardTitle}>{t('governance.risks.topRisksTitle', 'Top risks')}</h3>
          </CardHeader>
          <CardContent>
            {topRisks.length > 0 ? (
              <div className="space-y-3">
                {topRisks.map((risk, idx) => (
                  <div key={risk.id} className="flex items-start justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">#{idx + 1}</span>
                        <span className="font-medium">{risk.title}</span>
                        <span className={`${listStyles.riskLevelPill} ${getRiskLevelPillClass(risk.level)}`}>
                          Level {risk.level}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {risk.likelihood}/5 × {risk.impact}/5 = {risk.level}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      iconOnly
                      icon={<Eye className="w-4 h-4" />}
                      onClick={() => handleView(risk)}
                      className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-blue-600"
                      title={t('governance.risks.view', 'View details')}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No top risks found</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <h3 className={listStyles.chartCardTitle}>{t('governance.risks.trendTitle', 'Risk trend')}</h3>
        </CardHeader>
        <CardContent>
          {trendlineData && trendlineData.historical && trendlineData.historical.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={[...trendlineData.historical, ...(trendlineData.projection || [])]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#56a3d9" 
                  strokeWidth={2} 
                  name={t('governance.risks.trend.total', 'Total Risks')}
                  dot={{ r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="avgLevel" 
                  stroke="#F59E0B" 
                  strokeWidth={2} 
                  name={t('governance.risks.trend.avgLevel', 'Avg Risk Level')}
                  strokeDasharray="5 5"
                  dot={{ r: 4 }}
                />
                {trendlineData.projection && trendlineData.projection.length > 0 && (
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#56a3d9" 
                    strokeWidth={2} 
                    strokeDasharray="5 5"
                    name={t('governance.risks.trend.projection', 'Projection')}
                    data={trendlineData.projection}
                    dot={{ r: 3, fill: '#56a3d9' }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">{t('governance.risks.trend.noData', 'No trend data available')}</div>
          )}
        </CardContent>
      </Card>

      <GovernanceListingSection
        title={t('governance.risks.list', 'Risks List')}
        description={t('governance.risks.listSubtitle', 'Filter by status, level, dates, or search by title.')}
        toolbar={
          <div className={listStyles.filtersRow}>
            <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
              <SearchField
                label={t('governance.risks.searchLabel', 'Search')}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('governance.risks.searchPlaceholder', 'Search risks...')}
                minWidth="14rem"
              />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.risks.table.status', 'Status')}
                minWidth="11rem"
                placeholder={t('governance.risks.filterStatus', 'All Statuses')}
                options={statusFilterOptions}
                value={statusFilter === '' ? null : statusFilter}
                onChange={(v) => setStatusFilter(v ?? '')}
                disabled={statusFilterOptions.length === 0}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.risks.table.level', 'Risk level')}
                minWidth="12rem"
                placeholder={t('governance.risks.filterLevel', 'All Risk Levels')}
                options={GOVERNANCE_RISKS_LEVEL_FILTER_OPTIONS}
                value={levelFilter === '' ? null : levelFilter}
                onChange={(v) => setLevelFilter(v ?? '')}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-risks-from">
                {t('governance.risks.fromDate', 'From')}
              </label>
              <input
                id="governance-risks-from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-risks-to">
                {t('governance.risks.toDate', 'To')}
              </label>
              <input
                id="governance-risks-to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            {(statusFilter || levelFilter || searchQuery || fromDate || toDate) && (
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  icon={<X className={listStyles.badgeIcon} aria-hidden />}
                  onClick={clearFilters}
                >
                  {t('governance.risks.clearFilters', 'Clear')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.title', 'Title')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.likelihood', 'Likelihood')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.impact', 'Impact')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.level', 'Risk Level')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.status', 'Status')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.owner', 'Owner')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.nextReview', 'Next Review')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.risks.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableLoadingRow colSpan={8}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              )}
              {!loading && risks.length === 0 && (
                <TableEmptyRow colSpan={8}>
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" aria-hidden />
                  <p className="m-0">{t('governance.risks.noRisks', 'No risks found. Create your first risk to get started.')}</p>
                </TableEmptyRow>
              )}
              {!loading &&
                risks.map((risk) => (
                  <tr key={risk.id} className={listStyles.row}>
                    <td className={listStyles.td}>
                      <div className={listStyles.cellStrong}>{risk.title}</div>
                      {risk.description && <div className={listStyles.cellMuted}>{risk.description}</div>}
                    </td>
                    <td className={listStyles.td}>{risk.likelihood}/5</td>
                    <td className={listStyles.td}>{risk.impact}/5</td>
                    <td className={listStyles.td}>
                      <span className={`${listStyles.riskLevelPill} ${getRiskLevelPillClass(risk.level)}`}>
                        {risk.level} — {getRiskLevelLabel(risk.level)}
                      </span>
                    </td>
                    <td className={listStyles.td}>
                      <span className={`${listStyles.badge} ${getRiskStatusBadgeClass(risk.status)}`}>{risk.status}</span>
                    </td>
                    <td className={listStyles.td}>{risk.ownerName || '—'}</td>
                    <td className={listStyles.td}>
                      {risk.nextReview ? new Date(risk.nextReview).toLocaleDateString() : '—'}
                    </td>
                    <td className={listStyles.td}>
                      <div className={listStyles.actionsGap}>
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Eye className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => handleView(risk)}
                          className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-blue-600"
                          title={t('governance.risks.view', 'View details')}
                        />
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => handleEdit(risk)}
                          className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-green-600"
                          title={t('governance.risks.edit', 'Edit')}
                        />
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => handleDeleteRisk(risk.id)}
                          className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-red-600"
                          title={t('governance.risks.delete', 'Delete')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>

      {/* Modals will be added in next step */}
      {createModalOpen && (
        <CreateRiskModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false)
            setSelectedRisk(null)
          }}
          onSave={handleSave}
          users={users}
        />
      )}

      {editModalOpen && selectedRisk && (
        <EditRiskModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setSelectedRisk(null)
          }}
          onSave={handleSave}
          risk={selectedRisk}
          users={users}
        />
      )}

      {viewModalOpen && selectedRisk && (
        <ViewRiskModal
          isOpen={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false)
            setSelectedRisk(null)
            setShowSuggestions(false)
            setMitigationSuggestions([])
          }}
          risk={selectedRisk}
          onLoadSuggestions={() => loadMitigationSuggestions(selectedRisk.id)}
          suggestions={showSuggestions ? mitigationSuggestions : []}
        />
      )}
    </PageLayout>
  )
}

// Placeholder modals - will be implemented next
const CreateRiskModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  users: Array<{ id: string; username: string; email?: string }>
}> = ({ isOpen, onClose, onSave, users }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const riskStatusOptions = useMemo(
    () => [
      { value: 'Open', label: t('governance.risks.risk.form.statusOpen', 'Open') },
      { value: 'Mitigating', label: t('governance.risks.risk.form.statusMitigating', 'Mitigating') },
      { value: 'Accepted', label: t('governance.risks.risk.form.statusAccepted', 'Accepted') },
      { value: 'Closed', label: t('governance.risks.risk.form.statusClosed', 'Closed') },
    ],
    [t],
  )
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    likelihood: 1,
    impact: 1,
    ownerName: '',
    ownerId: '',
    status: 'Open',
    nextReview: ''
  })
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      alert(t('governance.risks.risk.form.titleRequired', 'Title is required'))
      return
    }

    setSaving(true)
    try {
      const payload: any = {
        title: formData.title,
        description: formData.description || undefined,
        likelihood: formData.likelihood,
        impact: formData.impact,
        status: formData.status,
        ownerName: formData.ownerName || undefined,
        ownerId: formData.ownerId || undefined,
        nextReview: formData.nextReview || undefined
      }

      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISKS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || t('governance.risks.risk.saveError', 'Failed to save risk'))
      }

      onSave()
    } catch (err: any) {
      alert(err.message || t('governance.risks.risk.saveError', 'Failed to save risk'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{t('governance.risks.risk.createTitle', 'New Risk')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.title', 'Title')} *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.description', 'Description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border rounded"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('governance.risks.risk.form.likelihood', 'Likelihood (1-5)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.likelihood}
                  onChange={(e) => setFormData({ ...formData, likelihood: parseInt(e.target.value) || 1 })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('governance.risks.risk.form.impact', 'Impact (1-5)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.impact}
                  onChange={(e) => setFormData({ ...formData, impact: parseInt(e.target.value) || 1 })}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.status', 'Status')}
              </label>
              <Select<string>
                className="w-full"
                minWidth="100%"
                options={riskStatusOptions}
                value={formData.status}
                onChange={(v) => v && setFormData({ ...formData, status: v })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.ownerName', 'Owner Name')}
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.nextReview', 'Next Review')}
              </label>
              <input
                type="date"
                value={formData.nextReview}
                onChange={(e) => setFormData({ ...formData, nextReview: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" color="outline" size="small" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" color="primary" size="small" disabled={saving}>
              {saving ? t('common.saving', 'Saving...') : t('common.create', 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditRiskModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  risk: Risk
  users: Array<{ id: string; username: string; email?: string }>
}> = ({ isOpen, onClose, onSave, risk, users }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const riskStatusOptions = useMemo(
    () => [
      { value: 'Open', label: t('governance.risks.risk.form.statusOpen', 'Open') },
      { value: 'Mitigating', label: t('governance.risks.risk.form.statusMitigating', 'Mitigating') },
      { value: 'Accepted', label: t('governance.risks.risk.form.statusAccepted', 'Accepted') },
      { value: 'Closed', label: t('governance.risks.risk.form.statusClosed', 'Closed') },
    ],
    [t],
  )
  const [formData, setFormData] = useState({
    title: risk.title,
    description: risk.description || '',
    likelihood: risk.likelihood,
    impact: risk.impact,
    ownerName: risk.ownerName || '',
    ownerId: risk.ownerId || '',
    status: risk.status,
    nextReview: risk.nextReview ? new Date(risk.nextReview).toISOString().split('T')[0] : ''
  })
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      alert(t('governance.risks.risk.form.titleRequired', 'Title is required'))
      return
    }

    setSaving(true)
    try {
      const payload: any = {
        title: formData.title,
        description: formData.description || undefined,
        likelihood: formData.likelihood,
        impact: formData.impact,
        status: formData.status,
        ownerName: formData.ownerName || undefined,
        ownerId: formData.ownerId || undefined,
        nextReview: formData.nextReview || undefined
      }

      const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISK(risk.id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || t('governance.risks.risk.saveError', 'Failed to save risk'))
      }

      onSave()
    } catch (err: any) {
      alert(err.message || t('governance.risks.risk.saveError', 'Failed to save risk'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{t('governance.risks.risk.editTitle', 'Edit Risk')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.title', 'Title')} *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.description', 'Description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border rounded"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('governance.risks.risk.form.likelihood', 'Likelihood (1-5)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.likelihood}
                  onChange={(e) => setFormData({ ...formData, likelihood: parseInt(e.target.value) || 1 })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('governance.risks.risk.form.impact', 'Impact (1-5)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.impact}
                  onChange={(e) => setFormData({ ...formData, impact: parseInt(e.target.value) || 1 })}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.status', 'Status')}
              </label>
              <Select<string>
                className="w-full"
                minWidth="100%"
                options={riskStatusOptions}
                value={formData.status}
                onChange={(v) => v && setFormData({ ...formData, status: v })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.ownerName', 'Owner Name')}
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.risks.risk.form.nextReview', 'Next Review')}
              </label>
              <input
                type="date"
                value={formData.nextReview}
                onChange={(e) => setFormData({ ...formData, nextReview: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" color="outline" size="small" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" color="primary" size="small" disabled={saving}>
              {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

const ViewRiskModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  risk: Risk
  onLoadSuggestions?: () => void
  suggestions?: any[]
}> = ({ isOpen, onClose, risk, onLoadSuggestions, suggestions = [] }) => {
  const { t } = useI18n()

  if (!isOpen) return null

  const modalLevelClass =
    risk.level >= 20
      ? listStyles.riskLevelCritical
      : risk.level >= 15
        ? listStyles.riskLevelHigh
        : risk.level >= 10
          ? listStyles.riskLevelMedium
          : risk.level >= 5
            ? listStyles.riskLevelLow
            : listStyles.riskLevelVeryLow

  const getRiskLevelLabel = (level: number) => {
    if (level >= 20) return 'Critical'
    if (level >= 15) return 'High'
    if (level >= 10) return 'Medium'
    if (level >= 5) return 'Low'
    return 'Very Low'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{risk.title}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('governance.risks.risk.form.description', 'Description')}
            </label>
            <p className="text-gray-900">{risk.description || '-'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                {t('governance.risks.risk.form.likelihood', 'Likelihood (1-5)')}
              </label>
              <p className="text-gray-900">{risk.likelihood}/5</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                {t('governance.risks.risk.form.impact', 'Impact (1-5)')}
              </label>
              <p className="text-gray-900">{risk.impact}/5</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('governance.risks.table.level', 'Risk Level')}
            </label>
            <span className={`${listStyles.riskLevelPill} ${modalLevelClass}`}>
              {risk.level} - {getRiskLevelLabel(risk.level)}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('governance.risks.table.status', 'Status')}
            </label>
            <span
              className={`${listStyles.badge} ${
                risk.status === 'Closed'
                  ? listStyles.badgeRiskClosed
                  : risk.status === 'Mitigating'
                    ? listStyles.badgeRiskMitigating
                    : risk.status === 'Accepted'
                      ? listStyles.badgeRiskAccepted
                      : risk.status === 'Open'
                        ? listStyles.badgeRiskOpen
                        : listStyles.badgeDefault
              }`}
            >
              {risk.status}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('governance.risks.table.owner', 'Owner')}
            </label>
            <p className="text-gray-900">{risk.ownerName || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('governance.risks.table.nextReview', 'Next Review')}
            </label>
            <p className="text-gray-900">
              {risk.nextReview ? new Date(risk.nextReview).toLocaleDateString() : '-'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
            <div>
              <label className="block font-medium mb-1">Created</label>
              <p>{new Date(risk.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <label className="block font-medium mb-1">Updated</label>
              <p>{new Date(risk.updatedAt).toLocaleString()}</p>
            </div>
          </div>
          
          {/* Mitigation Suggestions */}
          {onLoadSuggestions && (
            <div className="mt-6 border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{t('governance.risks.mitigationSuggestions', 'Mitigation Suggestions')}</h3>
                {suggestions.length === 0 && (
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    icon={<AlertTriangle className="w-4 h-4" />}
                    onClick={onLoadSuggestions}
                  >
                    {t('governance.risks.loadSuggestions', 'Load Suggestions')}
                  </Button>
                )}
              </div>
              
              {suggestions.length > 0 && (
                <div className="space-y-4">
                  {suggestions.map((suggestion, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          suggestion.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                          suggestion.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {suggestion.priority}
                        </span>
                        <h4 className="font-semibold">{suggestion.title}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{suggestion.description}</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {suggestion.actions.map((action: string, actionIdx: number) => (
                          <li key={actionIdx} className="text-gray-700">{action}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end mt-6">
          <Button type="button" color="outline" size="small" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GovernanceRisks
